import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Filter, MapPin, Users, ImageIcon, Sparkles } from "lucide-react";
import { api, BID_CAP } from "@/lib/api";
import type { Case } from "@shitamy/types";

const PREFS = ["", "東京都", "神奈川県", "静岡県"];
const AC_TYPES: Array<{ value: string; label: string }> = [
  { value: "", label: "全種別" },
  { value: "room", label: "ルーム" },
  { value: "package", label: "パッケージ" },
  { value: "multi", label: "マルチ" },
  { value: "commercial", label: "業務用" },
];
interface Props { onOpen: (id: string) => void; }

// 並べ替え：新着順 / 古い順 / 未入札優先（＝狙い目：まだ誰も入札していない古い案件を上に）
type SortMode = "new" | "old" | "unbid";
const SORT_LABEL: Record<SortMode, string> = { new: "新着順", old: "古い順", unbid: "未入札を優先" };
const SORT_NEXT: Record<SortMode, SortMode> = { new: "old", old: "unbid", unbid: "new" };

export function CaseListPage({ onOpen }: Props) {
  const [pref, setPref] = useState("");
  const [acType, setAcType] = useState("");
  const [sortMode, setSortMode] = useState<SortMode>("new");

  // サーバ側の並びは新着/古いのみ。未入札優先はクライアントで応札件数を見て並べ替える。
  const serverOrder: "new" | "closing" = sortMode === "old" ? "closing" : "new";
  const { data, isLoading, error } = useQuery({
    queryKey: ["open-cases", pref, acType, serverOrder],
    queryFn: () => api.listOpenCases({ pref: pref || undefined, ac_type: acType || undefined, order: serverOrder }),
    refetchInterval: 30_000,
  });

  // 応札枠（残り）表示用：表示中の案件の pending 応札件数をまとめて取得
  const caseIds = (data ?? []).map((c) => c.id);
  const { data: bidCounts } = useQuery({
    queryKey: ["bid-counts", caseIds],
    queryFn: () => api.casePendingCounts(caseIds),
    enabled: caseIds.length > 0,
    refetchInterval: 30_000,
  });

  // 未入札優先：未入札(0件)を先頭に、その中でも古い案件から（＝取りこぼしの狙い目）。
  const cases = useMemo(() => {
    const list = data ?? [];
    if (sortMode !== "unbid") return list;
    return [...list].sort((a, b) => {
      const ua = bidCounts?.[a.id] ?? 0;
      const ub = bidCounts?.[b.id] ?? 0;
      const za = ua === 0 ? 0 : 1;
      const zb = ub === 0 ? 0 : 1;
      if (za !== zb) return za - zb; // 未入札を上に
      return new Date(a.created_at).getTime() - new Date(b.created_at).getTime(); // 古い順
    });
  }, [data, bidCounts, sortMode]);

  // 代表写真（1枚目）を一覧カードのサムネイルとしてまとめて取得（N+1回避）
  const { data: thumbUrls } = useQuery({
    queryKey: ["case-thumbs", caseIds],
    queryFn: () => api.firstCasePhotoUrls(caseIds),
    enabled: caseIds.length > 0,
    refetchInterval: 300_000,
  });

  return (
    <div className="px-4 py-4 space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <Filter size={16} className="text-ink-soft" />
        <select
          value={pref}
          onChange={(e) => setPref(e.target.value)}
          className="text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-bg)] px-2 py-1"
        >
          {PREFS.map((p) => <option key={p} value={p}>{p || "全国"}</option>)}
        </select>
        <select
          value={acType}
          onChange={(e) => setAcType(e.target.value)}
          className="text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-bg)] px-2 py-1"
        >
          {AC_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <button
          onClick={() => setSortMode(SORT_NEXT[sortMode])}
          className="text-xs px-2 py-1 rounded-md border border-[var(--color-border)] text-ink-soft dark:text-white/70"
        >
          {SORT_LABEL[sortMode]}
        </button>
      </div>

      {isLoading && <p className="text-sm text-ink-soft">読み込み中...</p>}
      {error && <p className="text-sm text-red-600">読み込みに失敗しました</p>}
      {data && data.length === 0 && (
        <p className="text-sm text-ink-soft">条件に合う募集中の案件はありません。</p>
      )}

      <ul className="space-y-3">
        {cases.map((c) => {
          const used = bidCounts?.[c.id] ?? 0;
          const remaining = Math.max(BID_CAP - used, 0);
          const full = remaining <= 0;
          // 未入札で時間が経っている案件は「狙い目」（締切で急かすのではなく機会として見せる）
          const ageH = (Date.now() - new Date(c.created_at).getTime()) / 3_600_000;
          const isOpportunity = used === 0 && ageH >= 24 && !full;
          return (
          <li key={c.id}>
            {/* 旧デモUI: 白カード＋情報チップ（満枠はグレーアウト） */}
            <button
              onClick={() => onOpen(c.id)}
              className={`w-full bg-white dark:bg-white/5 p-4 rounded-xl border border-[var(--color-border)] text-left active:scale-95 transition ${full ? "opacity-60" : ""}`}
            >
              <div className="flex items-start gap-3">
                {/* 左：案件情報 */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      {c.customer_name && (
                        <p className="text-[11px] font-bold text-brand-700 dark:text-brand-300">{c.customer_name}案件</p>
                      )}
                      <p className="font-bold text-sm">{acTypeLabel(c.ac_type)}の入替・設置</p>
                      <p className="flex items-center gap-1 mt-1 text-xs text-ink-soft dark:text-white/60">
                        <MapPin size={11} />
                        {[c.prefecture, c.city].filter(Boolean).join(" ") || "エリア未設定"}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-2 text-xs flex-wrap">
                    {chipsOf(c).map((chip) => (
                      <span key={chip} className="px-2 py-0.5 bg-gray-100 dark:bg-white/10 rounded text-ink-soft dark:text-white/70">{chip}</span>
                    ))}
                  </div>
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <BidSlotBadge remaining={remaining} full={full} />
                    <span className="text-xs text-ink-soft dark:text-white/50">{agoLabel(c.created_at)}</span>
                  </div>
                </div>
                {/* 右：状態バッジ（締切で急かさない。未入札で古い案件は「狙い目」）＋代表写真サムネイル */}
                <div className="flex flex-col items-end gap-2 shrink-0">
                  {isOpportunity ? (
                    <span className="flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 font-bold">
                      <Sparkles size={11} /> 狙い目
                    </span>
                  ) : (
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-brand-50 text-brand-600 dark:bg-brand-800/30 dark:text-brand-200">
                      募集中
                    </span>
                  )}
                  <CaseThumb url={thumbUrls?.[c.id]} />
                  <span className={`text-xs font-bold ${full ? "text-ink-soft dark:text-white/40" : "text-brand-600 dark:text-brand-300"}`}>
                    {full ? "満員（閲覧のみ）" : "詳細を見る →"}
                  </span>
                </div>
              </div>
            </button>
          </li>
          );
        })}
      </ul>
    </div>
  );
}

/** 代表写真サムネイル。写真が無い/読込前はプレースホルダを表示。 */
function CaseThumb({ url }: { url?: string }) {
  return (
    <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-100 dark:bg-white/10 border border-[var(--color-border)] flex items-center justify-center shrink-0">
      {url ? (
        <img src={url} alt="案件写真" className="w-full h-full object-cover" loading="lazy" />
      ) : (
        <ImageIcon size={22} className="text-ink-soft/40 dark:text-white/30" />
      )}
    </div>
  );
}

/** 情報チップ：顧客入力を優先、なければAI推定。 */
function chipsOf(c: Case): string[] {
  const s = c.customer_specs;
  return [
    s?.model_number || c.ai_model_guess,
    s?.install_form,
    s?.refrigerant,
    s?.capacity || c.ai_capacity,
  ].filter((v): v is string => !!v).slice(0, 4);
}

/** 経過時間ラベル */
function agoLabel(createdAt: string): string {
  const h = (Date.now() - new Date(createdAt).getTime()) / 3_600_000;
  if (h < 1) return "1時間以内";
  if (h < 24) return `${Math.floor(h)}時間前`;
  return `${Math.floor(h / 24)}日前`;
}

/** 応札枠バッジ（残りn社 / 満員）。早い者勝ち3社まで（sql/10）。 */
function BidSlotBadge({ remaining, full }: { remaining: number; full: boolean }) {
  return (
    <span
      className={`flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full font-bold ${
        full
          ? "bg-gray-100 text-ink-soft dark:bg-white/10 dark:text-white/50"
          : remaining === 1
          ? "bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-300"
          : "bg-brand-50 text-brand-700 dark:bg-brand-800/30 dark:text-brand-200"
      }`}
    >
      <Users size={11} /> {full ? "応札枠 満員" : `応札枠 残り${remaining}`}
    </span>
  );
}

function acTypeLabel(t?: string | null) {
  switch (t) {
    case "room": return "ルームエアコン";
    case "package": return "パッケージエアコン";
    case "multi": return "マルチエアコン";
    case "commercial": return "業務用エアコン";
    default: return "エアコン工事";
  }
}
